import Button from '@sportnet/ui/Button';
import Col, { Row } from '@sportnet/ui/Grid';
import Modal, { ModalActions, ModalContent } from '@sportnet/ui/Modal';
import Paginator from '@sportnet/ui/Paginator';
import Segment from '@sportnet/ui/Segment';
import SegmentHeader from '@sportnet/ui/Segment/Header';
import { Table, Tbody, Td, Th, Thead, Tr } from '@sportnet/ui/Table';
import AppContext from '@sportnet/ui/TheLayout/AppContext';
import { ContextBar, ContextBarItem } from '@sportnet/ui/ContextBar';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory, useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import Api from '../../Api';
import Layout from '../../components/Layout';
import { __ } from '../../utilities';
import {
  getInvitationState,
  formatDateTime,
  isExpired,
  MY_SPORTNET_INVITATION_URL,
} from './helpers';

const LIST_LIMIT = 50;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  color: white;
  background-color: ${({ color }) => color};
`;

const ErrorMessage = styled.span`
  color: #dc3545;
`;

const ClickableTr = styled(Tr)`
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
  }
`;

const Muted = styled.div`
  color: #888;
  font-size: 12px;
`;

const ExpiredFlag = styled.span`
  color: #dc3545;
  font-size: 12px;
  font-weight: bold;
`;

const CleanATag = styled.a`
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  transition: all 200ms;
  &:hover {
    color: ${({ theme }) => theme.color.primary};
    text-decoration: none;
  }
`;

const FieldLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #888;
  margin-bottom: 2px;
`;

const FieldValue = styled.div`
  font-size: 14px;
  word-break: break-word;
  white-space: ${({ $wrap }) => ($wrap ? 'pre-wrap' : 'normal')};
`;

const FieldWrapper = styled.div`
  margin-bottom: 16px;
`;

const IdLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Field = ({ label, wrap, children }) => (
  <FieldWrapper>
    <FieldLabel>{label}</FieldLabel>
    <FieldValue $wrap={wrap}>{children}</FieldValue>
  </FieldWrapper>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  wrap: PropTypes.bool,
  children: PropTypes.node,
};

const Invitations = () => {
  const { appspace } = useParams();
  const location = useLocation();
  const history = useHistory();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const offset = Number(searchParams.get('offset')) || 0;

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);

  const closeDetail = () => {
    setSelected(null);
    setCopied(false);
  };

  const copyLink = async url => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Chyba pri kopírovaní:', err); // eslint-disable-line no-console
    }
  };

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await Api.crmGetInvitations(appspace, {
        offset,
        limit: LIST_LIMIT,
      });
      setInvitations(response.invitations || []);
      setTotal(response.total || 0);
    } catch (e) {
      if (e.status === 404) {
        setError(__('Pozvánky neboli nájdené.'));
      } else {
        setError(
          e.details?.userinfo || __('Pri získavaní pozvánok nastala chyba.'),
        );
      }
      setInvitations([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [appspace, offset]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChangeOffset = newOffset => {
    const params = new URLSearchParams(location.search);
    params.set('offset', String(newOffset));
    history.push({ search: params.toString() });
  };

  const renderTable = () => {
    if (error) {
      return <ErrorMessage>{error}</ErrorMessage>;
    }

    if (!isLoading && invitations.length === 0) {
      return <div>{__('Žiadne pozvánky')}</div>;
    }

    return (
      <Table>
        <Thead>
          <Tr>
            <Th>{__('Odoslaná')}</Th>
            <Th>{__('Aplikácia')}</Th>
            <Th>{__('Názov')}</Th>
            <Th>{__('E-mail')}</Th>
            <Th>{__('Stav')}</Th>
            <Th>{__('Vybavená')}</Th>
            <Th>{__('Platnosť do')}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {invitations.map(item => {
            const state = getInvitationState(item);
            const resolvedDt = item.resolved && item.resolved.dt;
            return (
              <ClickableTr key={item._id} onClick={() => setSelected(item)}>
                <Td>{formatDateTime(item.createdDT)}</Td>
                <Td>{item.appId}</Td>
                <Td>{item.title}</Td>
                <Td>{item.email}</Td>
                <Td>
                  <StatusBadge color={state.color}>{state.label}</StatusBadge>
                </Td>
                <Td>
                  {resolvedDt || item.sportnetId ? (
                    <>
                      <div>{formatDateTime(resolvedDt)}</div>
                      {item.sportnetId && <Muted>{item.sportnetId}</Muted>}
                    </>
                  ) : (
                    '-'
                  )}
                </Td>
                <Td>
                  {item.expiration ? (
                    <>
                      <div>{formatDateTime(item.expiration)}</div>
                      {isExpired(item) && (
                        <ExpiredFlag>{__('Expirovaná')}</ExpiredFlag>
                      )}
                    </>
                  ) : (
                    __('Bez obmedzenia')
                  )}
                </Td>
              </ClickableTr>
            );
          })}
        </Tbody>
      </Table>
    );
  };

  const renderDetail = () => {
    if (!selected) {
      return null;
    }
    const state = getInvitationState(selected);
    const resolvedDt = selected.resolved && selected.resolved.dt;
    const invitationUrl = `${MY_SPORTNET_INVITATION_URL}/${selected._id}`;

    return (
      <>
        <SegmentHeader size="s">{__('Stav a termíny')}</SegmentHeader>
        <Row>
          <Col xs={12} s={6} m={4}>
            <Field label={__('Stav')}>
              <StatusBadge color={state.color}>{state.label}</StatusBadge>
            </Field>
          </Col>
          <Col xs={12} s={6} m={4}>
            <Field label={__('Aplikácia')}>{selected.appId || '-'}</Field>
          </Col>
          <Col xs={12} s={6} m={4}>
            <Field label={__('Odoslaná')}>
              {formatDateTime(selected.createdDT)}
            </Field>
          </Col>
          <Col xs={12} s={6} m={4}>
            <Field label={__('Platnosť do')}>
              {selected.expiration
                ? formatDateTime(selected.expiration)
                : __('Bez obmedzenia')}
            </Field>
          </Col>
          <Col xs={12} s={6} m={4}>
            <Field label={__('Vybavená')}>
              {resolvedDt ? formatDateTime(resolvedDt) : '-'}
            </Field>
          </Col>
          <Col xs={12} s={6} m={4}>
            <Field label={__('Vybavil (sportnetId)')}>
              {selected.sportnetId ? (
                <CleanATag
                  as={Link}
                  to={`/admin/${appspace}/users/${selected.sportnetId}/profile`}
                >
                  {selected.sportnetId}
                </CleanATag>
              ) : (
                '-'
              )}
            </Field>
          </Col>
        </Row>

        <SegmentHeader size="s">{__('Text pozvánky')}</SegmentHeader>
        <Field label={__('Názov')} wrap>
          {selected.title || '-'}
        </Field>
        <Field label={__('Popis')} wrap>
          {selected.description || '-'}
        </Field>
        <Field label={__('E-mail príjemcu')}>{selected.email || '-'}</Field>

        <SegmentHeader size="s">{__('Identifikátory a odkazy')}</SegmentHeader>
        <Field label={__('ID pozvánky (odkaz na preposlanie)')}>
          <IdLine>
            <CleanATag target="_blank" rel="noreferrer" href={invitationUrl}>
              {selected._id}
            </CleanATag>
            <Button
              type="button"
              basic
              icon="files"
              onClick={() => copyLink(invitationUrl)}
            >
              {copied ? __('Skopírované') : __('Kopírovať odkaz')}
            </Button>
          </IdLine>
        </Field>
      </>
    );
  };

  return (
    <Layout
      bottomFixed={
        <ContextBar>
          <ContextBarItem>
            <Paginator
              offset={offset}
              limit={LIST_LIMIT}
              total={total}
              onChangeOffset={handleChangeOffset}
            />
          </ContextBarItem>
        </ContextBar>
      }
    >
      <AppContext title={__('Odoslané pozvánky')} />
      <Segment>
        <Segment raised loading={isLoading}>
          {renderTable()}
        </Segment>
      </Segment>

      <Modal isOpen={!!selected} size="l" handleClose={closeDetail}>
        <ModalContent>
          <SegmentHeader withSeparator>{__('Detail pozvánky')}</SegmentHeader>
          {renderDetail()}
        </ModalContent>
        <ModalActions style={{ justifyContent: 'flex-end' }}>
          <Button primary onClick={closeDetail}>
            {__('Zavrieť')}
          </Button>
        </ModalActions>
      </Modal>
    </Layout>
  );
};

export default Invitations;
